import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, User, ArrowRight, Wrench } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

function formatError(d) {
  if (!d) return "Login failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || JSON.stringify(d);
}

export default function AdminLogin() {
  const { admin, checking, login } = useAdminAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (checking) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center text-neutral-400 font-body">
        Loading…
      </div>
    );
  }
  if (admin) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] text-white font-body flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(245,197,24,0.08),_transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <span className="w-12 h-12 rounded-lg bg-[#F5C518] flex items-center justify-center">
            <Wrench className="w-6 h-6 text-[#121212]" strokeWidth={2.5} />
          </span>
        </div>

        <div className="glass-strong rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5C518] font-bold">
              Shop Owner Access
            </div>
            <h1 className="font-display font-extrabold text-3xl uppercase tracking-tighter mt-2">
              Admin Login
            </h1>
            <p className="text-sm text-neutral-400 mt-2">
              Sign in to manage Premium Auto Works bookings.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-400 mb-1.5 block font-semibold">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                  data-testid="admin-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm focus:border-[#F5C518] focus:outline-none focus:ring-1 focus:ring-[#F5C518]/30"
                  placeholder="admin"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-400 mb-1.5 block font-semibold">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm focus:border-[#F5C518] focus:outline-none focus:ring-1 focus:ring-[#F5C518]/30"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="px-3 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-300"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="glow-yellow w-full bg-[#F5C518] hover:bg-[#FFD642] disabled:opacity-60 disabled:cursor-not-allowed text-[#121212] font-display font-bold uppercase text-xs tracking-wider px-6 py-3.5 rounded-full flex items-center justify-center gap-2"
            >
              {loading ? "Signing in…" : "Sign In"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-neutral-500 mt-6">
          Forgot your password? Update <code className="text-[#F5C518]">ADMIN_PASSWORD_HASH</code> in
          backend/.env.
        </p>
      </div>
    </div>
  );
}
